import * as d3 from 'd3';
import React, { Suspense } from 'react';
import { css } from '@emotion/react';

import { Portal } from '$/components/Portal';
import { useWebinarPlayerContext } from '$/components/WebinarPlayer/context';

import formatTimeInWebinarSecs from '$/utils/formatTimeInWebinarSecs';

import { ZoomScaleProviderContext } from '../Timeline/ZoomScaleProviderContext';

const InteractionTypeDropdownField = React.lazy(() => import('./InteractionTypeDropdownField'));

const Cursor: React.FC<{ isEditingInteractions?: boolean }> = ({ isEditingInteractions }) => {
  const { zoom, zoomScale, zoomRef } = React.useContext(ZoomScaleProviderContext);
  const {
    colors,
    setPlaybackPosition,
    scrollCardsToPosition,
    playbackPosition,
    isBuffering,
    rooms,
    duration,
    interactionInEdit,
    timelineComponents,
  } = useWebinarPlayerContext();
  const panelWidth = 224;
  const cursorWidth = 40;
  const [isDragging, setIsDragging] = React.useState(false);
  const [draggingPosition, setDraggingPosition] = React.useState<number | null>(null);
  const timelineMenuRef = React.createRef<HTMLDivElement>();
  const xOffset = zoomScale!(draggingPosition ?? playbackPosition) ?? 1;
  const [hideCursor, setHideCursor] = React.useState(false);

  const ref = React.useCallback(
    (dom: SVGElement | null) => {
      if (!dom) return;
      const root = d3.select(dom);

      if (!isDragging && !isBuffering) {
        const width = zoom!.translateExtent()[1][0];
        if ((width && xOffset > width) || xOffset < 0) {
          setHideCursor(true);
          root.style('opacity', 0);
        } else {
          setHideCursor(false);
          root.style('opacity', 1);
        }

        root.raise().attr('x', `${xOffset - cursorWidth / 2}`);
      }

      const updateCursorPosition = (xOffset: number, inDrag?: boolean): void => {
        const appearAt = zoomScale?.invert(xOffset);
        if (typeof appearAt === 'undefined') return;

        const room = rooms!.getRoomAt(appearAt);
        if (!room.caps.inSession) {
          return;
        }

        root.raise().attr('x', `${xOffset - cursorWidth / 2}`);
        if (setPlaybackPosition && appearAt) {
          if (inDrag) {
            setDraggingPosition(appearAt);
          } else {
            let to = Math.ceil(appearAt);
            if (duration && appearAt > 0 && appearAt < duration) {
              to = appearAt; // Don't round within the video as video supports microseconds
            }
            setPlaybackPosition(to);
            scrollCardsToPosition(to);
          }
        }
      };

      d3.select(zoomRef!.current).on('click.cursor', (event) => {
        const translateSliderNode = document.querySelector('.translateSlider rect');
        if (event.target === translateSliderNode) return;

        const dimensions = zoomRef!.current!.getBoundingClientRect();
        const containerOffset = dimensions.left;
        const xOffset = event.x - containerOffset;
        updateCursorPosition(xOffset);
      });

      root.call(
        d3
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .drag<any, any, any>()
          .subject(() => zoomScale!(playbackPosition))
          .on('start', (_) => {
            root.style('cursor', 'grabbing');
            setIsDragging(true);
          })
          .on('end', (event) => {
            const width = zoom!.translateExtent()[1][0];

            let xOffset = event.x;
            if (width && xOffset > width) xOffset = width;
            if (xOffset < 0) xOffset = 0;

            updateCursorPosition(xOffset);

            root.style('cursor', 'grab');
            setIsDragging(false);

            // Give paint time to move currentPosition
            setTimeout(() => {
              setDraggingPosition(null);
            }, 100);
          })
          .on('drag', (event) => {
            const width = zoom!.translateExtent()[1][0];

            let xOffset = event.x;
            if (width && xOffset > width) xOffset = width;
            if (xOffset < 0) xOffset = 0;

            updateCursorPosition(xOffset, true);
          })
      );
      // .on("start", d => circle.filter(p => p === d).raise().attr("stroke", "black"))
    },
    [
      isDragging,
      isBuffering,
      zoomRef,
      zoom,
      xOffset,
      zoomScale,
      rooms,
      setPlaybackPosition,
      duration,
      scrollCardsToPosition,
      playbackPosition,
    ]
  );

  // const appearAt = Math.round(zoomScale.invert(Math.round(xOffset)))
  const cursorHeight = timelineComponents.time.height - 2;
  const cursorOffset = 1;
  return (
    <>
      <svg className='absolute overflow-visible top-0' width={zoom!.translateExtent()[1][0]}>
        <svg
          id='timelineCursor'
          ref={ref}
          y={0}
          x={0 - cursorWidth / 2}
          width={cursorWidth}
          className='overflow-visible top-0 relative pointer-events-all'
          css={css`
            color: ${colors.control};
            cursor: grab;
          `}
        >
          <g>
            <rect
              className='cursor-time-line'
              x={cursorWidth / 2 - 1}
              y={0}
              height={timelineComponents.time.offset + cursorOffset}
              width={2}
              fill={colors.control}
              r='0'
            />
            <svg
              x={cursorWidth / 2 - 45}
              y={
                interactionInEdit
                  ? 0 - timelineComponents.time.height
                  : timelineComponents.time.offset
              }
              alignmentBaseline='middle'
              width='90'
              height={timelineComponents.time.height}
              viewBox={`0 0 90 ${timelineComponents.time.height}`}
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <rect y={cursorOffset} width='90' height={cursorHeight} rx='9' fill='currentColor' />
              <path
                fill='white'
                cursor='pointer'
                transform={`translate(${cursorWidth - 22}, ${cursorOffset * -2})`}
                d='M66 11L57 16.1962L57 5.80385L66 11Z'
              />
              <path
                fill='white'
                transform={`translate(0, ${cursorOffset * -2})`}
                cursor='pointer'
                d='M5 11L14 16.1962L14 5.80385L5 11Z'
              />
              <text
                x={45}
                y={timelineComponents.time.height / 2 + 1}
                fill={colors.text}
                textAnchor={'middle'}
                alignmentBaseline='middle'
              >
                {formatTimeInWebinarSecs(draggingPosition ?? playbackPosition)}
              </text>
            </svg>
          </g>
        </svg>
      </svg>
      {!interactionInEdit && isEditingInteractions && (
        <Portal containerId='timelineTrack'>
          <div
            ref={timelineMenuRef}
            style={{
              opacity: hideCursor ? 0 : 1,
              left: xOffset > 93 ? xOffset - panelWidth / 2 : 93 - panelWidth / 2,
              top: -38,
            }}
            className='overflow-visible z-50 interaction-type-dropdown absolute pointer-events-all select-none'
          >
            <div
              style={{ pointerEvents: 'all' }}
              css={css`
                min-width: 16rem;
                max-width: 20rem;
              `}
            >
              <Suspense>
                <InteractionTypeDropdownField />
              </Suspense>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};

export default Cursor;
